import React, { useEffect, useState, useRef } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import MessengerService from '../../services/messengerService';
import { showConfirm, showSuccessDetail, showDeleteDetail, showError, showLoading, hideLoading } from '../../components/utils/swal-helpers';
import FormDatePicker from '../../components/common/form-date-picker';

const ROWS_PER_PAGE = 8;

const getPageNumbers = (currentPage, totalPages) => {
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, currentPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const TABS = [
    { key: 'all',      label: 'All'       },
    { key: 'internal', label: 'Internal'  },
    { key: 'outsource',label: 'Outsource' },
];

// ── กันเลือกวันเสาร์-อาทิตย์ ──
const isWeekday = (date) => {
    const day = new Date(date).getDay();
    return day !== 0 && day !== 6;
};

// ─────────────────────────────────────────────────────────
// Messenger Modal
// ─────────────────────────────────────────────────────────

const MessengerModal = ({ mode, data, onClose, onSave }) => {
    const [form, setForm] = useState({
        name:             data?.name             || '',
        surname:          data?.surname          || '',
        tel:              data?.tel              || '',
        email:            data?.email            || '',
        is_outsource:     data?.is_outsource     ?? 0,
        login_start_date: data?.login_start_date ? new Date(data.login_start_date) : new Date(),
        login_end_date:   data?.login_end_date   ? new Date(data.login_end_date)   : null,
    });

    const [endDateError, setEndDateError] = useState(false);

    const isEdit      = mode === 'edit';
    const isOutsource = form.is_outsource === 1;

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    // maxDate = start + 30 วัน
    const maxEndDate = form.login_start_date
        ? new Date(new Date(form.login_start_date).setDate(new Date(form.login_start_date).getDate() + 30))
        : null;

    const handleSave = () => {
        if (isOutsource && !form.login_end_date) {
            setEndDateError(true);
            return;
        }
        setEndDateError(false);

        // ── FIX: เดิมใช้ d.toISOString().slice(0, 10) ซึ่งแปลงเป็น UTC ก่อนตัด ──
        // ── date ที่ได้จาก FormDatePicker เป็นเวลาท้องถิ่น 00:00:00 (เช่น ไทย UTC+7) ──
        // ── พอแปลงเป็น UTC จะกลายเป็น 17:00 ของ "วันก่อนหน้า" ทำให้วันที่เพี้ยนถอยไป 1 วันเสมอ ──
        // ── (เลือก 26 กลายเป็น 25) แก้โดยดึง year/month/day จากเวลาท้องถิ่นตรงๆ ไม่ผ่าน toISOString ──
        const toStr = (d) => {
            if (!(d instanceof Date)) return d || '';
            const year  = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day   = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const payload = { ...form };
        if (isOutsource && form.login_start_date && form.login_end_date) {
            payload.login_start_date = toStr(form.login_start_date);
            payload.login_end_date   = toStr(form.login_end_date);
        } else {
            payload.login_start_date = '';
            payload.login_end_date   = '';
        }
        setTimeout(() => onSave(payload), 0);
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไข Messenger' : 'เพิ่มพนักงานขับ New Messenger'}</h3>
                </div>
                <div className="mc-modal-body">
                    <div className="border-line">
                        {/* ── ประเภทพนักงานขับ ── */}
                        <div className="mc-form-group mc-form-group--full mc-form-group-msg" style={{ marginBottom: 16 }}>
                            <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                                ประเภทพนักงานขับ (Messenger Type)
                            </label>
                            <div className="radio-group radio-group-msg">
                                <label className="radio-option radio-option-msg">
                                    <input
                                        type="radio"
                                        name="is_outsource"
                                        value={0}
                                        checked={form.is_outsource === 0}
                                        onChange={() => handleChange('is_outsource', 0)}
                                    />
                                    พนักงานประจำ (Internal)
                                </label>
                                <label className="radio-option radio-option-msg">
                                    <input
                                        type="radio"
                                        name="is_outsource"
                                        value={1}
                                        checked={form.is_outsource === 1}
                                        onChange={() => handleChange('is_outsource', 1)}
                                    />
                                    คนขับภายนอก (Outsource)
                                </label>
                            </div>
                        </div>

                        {/* ── วันที่ (แสดงเฉพาะ Outsource) ── */}
                        {isOutsource && (
                            <div className="mc-form-row" style={{ marginBottom: 16 }}>
                                <div className="mc-form-group">
                                    <label>วันที่เริ่มต้น (Start Date)</label>
                                    <FormDatePicker
                                        value={form.login_start_date}
                                        onChange={(date) => {
                                            handleChange('login_start_date', date);
                                            // reset end date ถ้า start เปลี่ยน
                                            handleChange('login_end_date', null);
                                        }}
                                        placeholderText="DD/MM/YYYY"
                                        filterDate={isWeekday}
                                    />
                                </div>
                                <div className="mc-form-group">
                                    <label>วันที่สิ้นสุด (End Date)</label>
                                    <FormDatePicker
                                        value={form.login_end_date}
                                        onChange={(date) => {
                                            handleChange('login_end_date', date);
                                            setEndDateError(false);
                                        }}
                                        placeholderText="DD/MM/YYYY"
                                        minDate={form.login_start_date || new Date()}
                                        maxDate={maxEndDate}
                                        isInvalid={endDateError}
                                        filterDate={isWeekday}
                                    />
                                    {endDateError && (
                                        <div className="invalid-feedback" style={{ display: 'block' }}>
                                            กรุณาเลือกวันสิ้นสุด
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    placeholder="กรอกชื่อ"
                                    value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>Surname</label>
                                <input
                                    type="text"
                                    placeholder="กรอกนามสกุล"
                                    value={form.surname}
                                    onChange={(e) => handleChange('surname', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>Tel.</label>
                                <input
                                    type="text"
                                    placeholder="000-000-0000"
                                    value={form.tel}
                                    onChange={(e) => handleChange('tel', e.target.value)}
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>E-mail</label>
                                <input
                                    type="text"
                                    placeholder="Email@example.co.th"
                                    value={form.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminMessengers = () => {
    const [data, setData]           = useState([]);
    const [search, setSearch]       = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [modal, setModal]         = useState(null);
    const [newId, setNewId]         = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const highlightRef = useRef(null);

    useEffect(() => {
        loadData();
        return () => { if (highlightRef.current) clearTimeout(highlightRef.current); };
    }, []);

    const loadData = async () => {
        try {
            const resp = await MessengerService.SearchMessenger({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            const sorted = [...resp.data.result].sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', 'th')
            );
            setData(sorted);
        } catch (err) { console.error(err); }
    };

    const handleSave = async (form) => {
        const { mode: currentMode, data: currentData } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไข Messenger' : 'ยืนยันการเพิ่ม Messenger ใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                name:             form.name,
                surname:          form.surname,
                tel:              form.tel,
                email:            form.email,
                is_outsource:     form.is_outsource,
                login_start_date: form.login_start_date || '',
                login_end_date:   form.login_end_date   || '',
                status:           1,
            };

            let createdId = null;
            if (currentMode === 'edit') {
                await MessengerService.UpdateMessenger(currentData?.id, payload);
            } else {
                const resp = await MessengerService.CreateMessenger(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขสำเร็จแล้ว' : 'เพิ่ม Messenger สำเร็จแล้ว',
            });

            await loadData();

            if (currentMode === 'add' && createdId) {
                setNewId(createdId);
                if (highlightRef.current) clearTimeout(highlightRef.current);
                highlightRef.current = setTimeout(() => setNewId(null), 10000);
            }
        } catch (err) {
            hideLoading();
            // ── ดึง error message จาก backend มาโชว์ให้ user เห็นสาเหตุจริง แทน error ทั่วไป ──
            const rawMsg = err?.response?.data?.message || '';

            // ── map ข้อความ backend ที่รู้จัก ให้เป็นภาษาไทยที่เข้าใจง่าย ──
            const KNOWN_ERRORS = {
                'this email already exists': 'อีเมลนี้มีอยู่ในระบบแล้ว กรุณาใช้อีเมลอื่น',
                'login period cannot exceed 30 days': 'ระยะเวลาการเข้าสู่ระบบไม่ควรเกิน 30 วัน',
            };
            const friendlyMsg = KNOWN_ERRORS[rawMsg] || rawMsg;
            if (friendlyMsg !== '') {
                showError(friendlyMsg);
            } else {
                showError();
            }
        }
    };

    const handleDelete = async (row) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบ Messenger',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await MessengerService.DeleteMessenger(row.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบ Messenger ออกแล้ว' });
            loadData();
        } catch (err) { hideLoading(); showError(); }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setSearch('');
        setCurrentPage(1);
    };

    const tabFiltered = data.filter((row) => {
        if (activeTab === 'internal')  return row.is_outsource === 0;
        if (activeTab === 'outsource') return row.is_outsource === 1;
        return true;
    });

    const filtered = tabFiltered.filter((row) => {
        const q = search.toLowerCase();
        if (!q) return true;
        return (
            (row.name    || '').toLowerCase().includes(q) ||
            (row.surname || '').toLowerCase().includes(q) ||
            (row.tel     || '').toLowerCase().includes(q) ||
            (row.email   || '').toLowerCase().includes(q)
        );
    });

    const countAll       = data.length;
    const countInternal  = data.filter((r) => r.is_outsource === 0).length;
    const countOutsource = data.filter((r) => r.is_outsource === 1).length;
    const tabCount       = { all: countAll, internal: countInternal, outsource: countOutsource };

    const totalPages  = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const safePage    = Math.min(currentPage, totalPages);
    const paged       = filtered.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);
    const pageNumbers = getPageNumbers(safePage, totalPages);

    const goPage = (p) => {
        if (p >= 1 && p <= totalPages) setCurrentPage(p);
    };

    const countLabel = filtered.length === 0
        ? '0 รายการ'
        : `${(safePage - 1) * ROWS_PER_PAGE + 1}-${Math.min(safePage * ROWS_PER_PAGE, filtered.length)} จาก ${filtered.length} รายการ`;

    const validDateLabel = (row) => {
        if (row.is_outsource !== 1) return 'Permanent';
        if (!row.login_start_date || !row.login_end_date) return '-';
        const fmtStr = (str) => {
            const [y, m, d] = str.slice(0, 10).split('-');
            return `${d}/${m}/${y}`;
        };
        return `${fmtStr(row.login_start_date)} ถึง ${fmtStr(row.login_end_date)}`;
    };

    return (
        <>
            <div className="mc-page">
                <div className="mc-header">
                    <div className="mc-header__left">
                        <h2 className="mc-title">Master Data - Messenger</h2>
                        <p className="mc-subtitle">รายชื่อพนักงานขับ</p>
                    </div>
                    <button className="mc-btn mc-btn--new" onClick={() => setModal({ mode: 'add' })}>
                        <i className="ri-add-line" /> New Messenger
                    </button>
                </div>

                <div className="mc-card">
                    <div className="sp-tabs">
                        <div className="sp-tabs__list">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    className={`sp-tab${activeTab === tab.key ? ' sp-tab--active' : ''}`}
                                    onClick={() => handleTabChange(tab.key)}
                                >
                                    {tab.label} {tabCount[tab.key]}
                                </button>
                            ))}
                        </div>
                        <div className="sp-tabs__right">
                            <span className="mc-count">{countLabel}</span>
                        </div>
                    </div>

                    <div className="mc-toolbar">
                        <div className="mc-search">
                            <i className="ri-search-line" />
                            <input
                                type="text"
                                placeholder="Select"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table className="mc-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>No.</th>
                                    <th>Name</th>
                                    <th>Surname</th>
                                    <th>Tel</th>
                                    <th>Email</th>
                                    <th>Type</th>
                                    <th>Valid Date</th>
                                    <th style={{ width: 100, textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="mc-table-empty">ไม่มีข้อมูล</td>
                                    </tr>
                                ) : (
                                    paged.map((row, idx) => (
                                        <tr key={row.id} className={row.id === newId ? 'mc-row-new' : ''}>
                                            <td className="mc-col-no">{(safePage - 1) * ROWS_PER_PAGE + idx + 1}</td>
                                            <td>{row.name}</td>
                                            <td>{row.surname}</td>
                                            <td className="mc-col-muted">{row.tel   || '-'}</td>
                                            <td className="mc-col-muted">{row.email || '-'}</td>
                                            <td>{row.is_outsource === 1 ? 'Outsource' : 'Internal'}</td>
                                            <td className="mc-col-muted">{validDateLabel(row)}</td>
                                            <td>
                                                <div className="mc-actions">
                                                    <button
                                                        className="mc-action-btn mc-action-btn--edit"
                                                        onClick={() => setModal({ mode: 'edit', data: row })}
                                                    >
                                                        <i className="ri-pencil-line" />
                                                    </button>
                                                    <button
                                                        className="mc-action-btn mc-action-btn--delete"
                                                        onClick={() => handleDelete(row)}
                                                    >
                                                        <i className="ri-delete-bin-line" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="table-pagination">
                        <Pagination>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink first onClick={() => goPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink previous onClick={() => goPage(safePage - 1)} />
                            </PaginationItem>
                            {pageNumbers.map((p) => (
                                <PaginationItem key={p} active={p === safePage}>
                                    <PaginationLink onClick={() => goPage(p)}>{p}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink next onClick={() => goPage(safePage + 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink last onClick={() => goPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </div>
            </div>

            {modal && (
                <MessengerModal
                    mode={modal.mode}
                    data={modal.data}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}
        </>
    );
};

export default AdminMessengers;